import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { Avatar } from "@/components/shared/Avatar";
import { cn } from "@/lib/utils";
import { formatDate, formatNOK } from "@/lib/format";
import { formatPhoneDisplay, normalizePhoneE164 } from "@/lib/phone-format";
import { ratingSummary, type FreelancerListRow } from "@/lib/freelancers";
import { quickContact } from "@/lib/freelancer-contacts";
import type { DBFreelancerContact, FreelancerProjectHistoryRow } from "@/lib/db-types";
import { RatingStars } from "./RatingStars";

function Fact({ label, children, sub, className }: { label: string; children: React.ReactNode; sub?: React.ReactNode; className?: string }) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      <dt className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
      {sub && <dd className="truncate text-xs text-muted-foreground">{sub}</dd>}
    </div>
  );
}

const Muted = ({ children }: { children: React.ReactNode }) => <span className="font-normal text-muted-foreground">{children}</span>;

/** «Showreel» først, så de andre i rekkefølge. */
function quickLinks(links: FreelancerListRow["links"]) {
  return [...links].sort((a, b) => Number(/showreel/i.test(b.label)) - Number(/showreel/i.test(a.label))).slice(0, 2);
}

export interface QuickActionsProps {
  freelancer: FreelancerListRow;
  contacts: DBFreelancerContact[];
  /** Mobil: bare Ring og E-post, store knapper. */
  compact?: boolean;
}

/** VIB-2934: Ring / E-post / inntil to lenker under navnet, og bunnlinjen på mobil. */
export function FreelancerQuickActions({ freelancer: f, contacts, compact = false }: QuickActionsProps) {
  const q = quickContact(f, contacts);
  const links = compact ? [] : quickLinks(f.links);
  if (!q.phone && !q.email && links.length === 0) return null;
  return (
    <div className={cn("flex flex-wrap gap-2", compact && "flex-nowrap")}>
      {q.phone && (
        <Button asChild size="sm" variant={compact ? "default" : "outline"} className={cn(compact && "flex-1")}>
          <a href={`tel:${normalizePhoneE164(q.phone) ?? q.phone}`} aria-label={`Ring ${q.callName ?? f.name} på ${formatPhoneDisplay(q.phone)}`}>
            <SrIcon name="phone" className="h-4 w-4" />
            {q.callName ? `Ring ${q.callName}` : "Ring"}
          </a>
        </Button>
      )}
      {q.email && (
        <Button asChild size="sm" variant="outline" className={cn(compact && "flex-1")}>
          <a href={`mailto:${q.email}`} aria-label={`Send e-post til ${q.emailName ?? f.name}`}>
            <SrIcon name="mail" className="h-4 w-4" />
            {q.emailName === "agent" ? "E-post til agent" : "E-post"}
          </a>
        </Button>
      )}
      {links.map((l) => (
        <Button key={l.url} asChild size="sm" variant="outline">
          <a href={l.url} target="_blank" rel="noreferrer">
            <SrIcon name="external-link" className="h-4 w-4" />
            {l.label}
          </a>
        </Button>
      ))}
    </div>
  );
}

function priceText(f: FreelancerListRow): string | null {
  const parts = [
    f.day_rate_nok != null ? `${formatNOK(f.day_rate_nok)}/dag` : null,
    f.hour_rate_nok != null ? `${formatNOK(f.hour_rate_nok)}/t` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

function priceSub(f: FreelancerListRow): string | undefined {
  if (f.rate_note) return f.rate_note;
  if (f.rate_updated_at) return `endret ${formatDate(f.rate_updated_at)}`;
  return f.monday_item_ids.length ? "fra Monday" : undefined;
}

interface Props {
  freelancer: FreelancerListRow;
  contacts: DBFreelancerContact[];
  /** null når frilans på prosjekt er av. */
  history: FreelancerProjectHistoryRow[] | null;
  actions: React.ReactNode;
}

/** Toppkortet på profilen: navn, fagfelt, hurtigknapper og bare de nøkkeltallene som finnes (VIB-2934). */
export function FreelancerHero({ freelancer: f, contacts, history, actions }: Props) {
  const { avg, count } = ratingSummary(f.ratings);
  const subline = [f.company_name, f.organization_number && `org.nr ${f.organization_number}`, f.location].filter(Boolean).join(" · ");
  const price = priceText(f);
  const used = history ?? [];
  const latest = used[0];
  const withVouchers = used.filter((r) => r.voucher_count > 0);
  const invoiced = withVouchers.reduce((s, r) => s + r.invoiced, 0);
  const negative = withVouchers.filter((r) => r.invoiced < 0);

  return (
    <section aria-label="Frilanser" className="flex flex-col gap-5 border-[1.5px] border-foreground bg-card px-6 py-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <Avatar name={f.name} size="lg" className={cn("shrink-0 border-[1.5px] border-foreground", f.kind === "company" && "rounded-md")} />
          <div className="min-w-0 space-y-1">
            <h1 className="page-title !mb-0 leading-tight">{f.name}</h1>
            {subline && <p className="text-sm text-muted-foreground">{subline}</p>}
            <div className="flex flex-wrap gap-1 pt-1">
              {f.kind === "company" && <Badge variant="outline" className="px-2 text-xs">Firma</Badge>}
              {f.archived_at && <Badge variant="secondary" className="px-2 text-xs">Arkivert</Badge>}
              {f.disciplines.map((d) => (
                <Badge key={d.id} variant="secondary" className="px-2 text-xs">{d.label}{d.is_primary && f.disciplines.length > 1 ? " (hoved)" : ""}</Badge>
              ))}
              {f.tags.map((t) => <Badge key={t} variant="outline" className="px-2 text-xs">{t}</Badge>)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">{actions}</div>
      </div>

      <div className="hidden md:block"><FreelancerQuickActions freelancer={f} contacts={contacts} /></div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-4 border-t border-border pt-4 md:grid-cols-4">
        <Fact
          label="Vurdering"
          sub={count > 0 ? `${count} vurdering${count === 1 ? "" : "er"}` : used.length > 0 ? "brukt på prosjekt" : "ingen vurderinger"}
        >
          {count > 0
            ? <span className="flex items-center gap-1.5"><RatingStars value={avg} /> {avg?.toLocaleString("nb-NO")}</span>
            : <Muted>{used.length > 0 ? "Ikke vurdert" : "Ikke testet"}</Muted>}
        </Fact>
        <Fact label="Pris" sub={price ? priceSub(f) : undefined}>
          {price ?? <Muted>{f.rate_note ?? "Ikke registrert"}</Muted>}
        </Fact>
        {history && (
          <Fact label="Prosjekter" sub={latest ? `siste: ${[latest.project_code, latest.project_name].filter(Boolean).join(" ")}` : undefined}>
            {used.length > 0 ? used.length : <Muted>Ingen ennå</Muted>}
          </Fact>
        )}
        {history && withVouchers.length > 0 && (
          <Fact
            label="Fakturert til oss"
            sub={`${withVouchers.length} prosjekt${withVouchers.length === 1 ? "" : "er"}${negative.length ? ` · sjekk ${negative.map((r) => r.project_code).join(", ")}` : ""}`}
          >
            <span className={cn(invoiced < 0 && "text-destructive")}>{formatNOK(invoiced)}</span>
          </Fact>
        )}
      </dl>
    </section>
  );
}
